/**
 * pushNotificationService.js — API calls for browser push subscription management
 *
 * Wraps the /users/push-subscription backend endpoints:
 *   GET    → fetch VAPID public key (needed before subscribing)
 *   POST   → register a new browser PushSubscription with the backend
 *   DELETE → unregister a subscription on logout or permission revoke
 *
 * Usage:
 *   import pushNotificationService from '@/services/services/pushNotificationService';
 *   const { vapid_public_key } = await pushNotificationService.getVapidPublicKey();
 *   await pushNotificationService.registerSubscription(pushSubscription);
 *   await pushNotificationService.unregisterSubscription(endpoint);
 */

import api from '@/core/api/api';

const PUSH_ENDPOINT = '/users/push-subscription';

const pushNotificationService = {
  /**
   * Fetch the VAPID public key from the backend.
   * Returns { vapid_public_key: string }
   */
  getVapidPublicKey: async () => {
    const response = await api.get(PUSH_ENDPOINT);
    return response.data;
  },

  /**
   * Register a browser PushSubscription object with the backend.
   * The subscription object comes from pwaService.subscribeToPush().
   * Returns { success: true, vapid_public_key: string }
   */
  registerSubscription: async (subscription) => {
    const subJson = subscription.toJSON();
    const response = await api.post(PUSH_ENDPOINT, {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      user_agent: navigator.userAgent,
    });
    return response.data;
  },

  /**
   * Remove a push subscription from the backend by endpoint URL.
   * Call this on logout or when the user revokes permission.
   */
  unregisterSubscription: async (endpoint) => {
    const response = await api.delete(PUSH_ENDPOINT, { data: { endpoint } });
    return response.data;
  },
};

export default pushNotificationService;
